import './PrivacyPolicy.css'

function TermsOfService() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: January 22, 2026</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            Welcome to UniqueBrains! By accessing or using our online learning platform at uniquebrains.org ("Platform"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Platform.
          </p>
          <p>
            These Terms constitute a legally binding agreement between you and UniqueBrains ("we," "us," or "our").
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>
            UniqueBrains provides an online learning platform that connects students with instructors for educational courses. Our services include:
          </p>
          <ul>
            <li>Course platform and enrollment</li>
            <li>Live online classes and recorded sessions</li>
            <li>Educational resources and materials</li>
            <li>Homework assignments and progress tracking</li>
            <li>Communication tools between students and instructors</li>
            <li>Support for neurodiverse learners</li>
          </ul>
        </section>

        <section>
          <h2>3. User Accounts</h2>
          
          <h3>3.1 Account Creation</h3>
          <p>To use certain features of our Platform, you must create an account. You agree to:</p>
          <ul>
            <li>Provide accurate, current, and complete information</li>
            <li>Maintain and update your information to keep it accurate</li>
            <li>Maintain the security of your password</li>
            <li>Accept responsibility for all activities under your account</li>
            <li>Notify us immediately of any unauthorized use</li>
          </ul>

          <h3>3.2 Account Types</h3>
          <p>We offer two types of accounts:</p>
          <ul>
            <li><strong>Student Accounts:</strong> For individual learners</li>
            <li><strong>Instructor Accounts:</strong> For educators creating and teaching courses</li>
          </ul>

          <h3>3.3 Age Requirements</h3>
          <p>
            Users under 18 should have parental consent to use the Platform.
          </p>
        </section>

        <section>
          <h2>4. User Conduct</h2>
          <p>You agree NOT to:</p>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe on intellectual property rights of others</li>
            <li>Upload malicious code, viruses, or harmful content</li>
            <li>Harass, bully, or threaten other users</li>
            <li>Share inappropriate or offensive content</li>
            <li>Impersonate others or misrepresent your identity</li>
            <li>Attempt to gain unauthorized access to the Platform</li>
            <li>Interfere with the proper functioning of the Platform</li>
            <li>Use the Platform for commercial purposes without authorization</li>
            <li>Share your account credentials with others</li>
          </ul>
        </section>

        <section>
          <h2>5. Course Enrollment and Payment</h2>
          
          <h3>5.1 Course Enrollment</h3>
          <p>
            When you enroll in a course, you agree to participate actively and respectfully. Course availability, schedules, and content are subject to change.
          </p>

          <h3>5.2 Payment Terms</h3>
          <ul>
            <li>Course fees are displayed at the time of enrollment</li>
            <li>Payment is required before accessing course content</li>
            <li>All fees are in USD unless otherwise stated</li>
            <li>We use secure third-party payment processors</li>
          </ul>

          <h3>5.3 Refund Policy</h3>
          <p>
            Refund requests must be submitted within 7 days of enrollment. Refunds are granted at our discretion based on:
          </p>
          <ul>
            <li>Course attendance and participation</li>
            <li>Amount of content accessed</li>
            <li>Reason for refund request</li>
          </ul>
          <p>
            No refunds are provided after 14 days of enrollment or after 50% of the course has been completed.
          </p>
        </section>

        <section>
          <h2>6. Instructor Responsibilities</h2>
          <p>If you are an instructor, you agree to:</p>
          <ul>
            <li>Provide accurate course descriptions and information</li>
            <li>Deliver courses as described and scheduled</li>
            <li>Maintain professional conduct with students</li>
            <li>Protect student privacy and data</li>
            <li>Provide quality educational content</li>
            <li>Respond to student questions and concerns promptly</li>
            <li>Comply with all applicable educational regulations</li>
          </ul>
        </section>

        <section>
          <h2>7. Intellectual Property</h2>
          
          <h3>7.1 Platform Content</h3>
          <p>
            All content on the Platform, including text, graphics, logos, images, and software, is owned by UniqueBrains or our licensors and is protected by copyright and other intellectual property laws.
          </p>

          <h3>7.2 Course Content</h3>
          <p>
            Instructors retain ownership of their course content but grant us a license to display, distribute, and promote their courses on the Platform.
          </p>

          <h3>7.3 User Content</h3>
          <p>
            You retain ownership of content you submit (assignments, messages, etc.) but grant us a license to use it as necessary to provide our services.
          </p>

          <h3>7.4 Restrictions</h3>
          <p>You may NOT:</p>
          <ul>
            <li>Copy, modify, or distribute Platform content without permission</li>
            <li>Record or share course materials without instructor consent</li>
            <li>Use content for commercial purposes</li>
            <li>Remove copyright or proprietary notices</li>
          </ul>
        </section>

        <section>
          <h2>8. Privacy and Data Protection</h2>
          <p>
            Your privacy is important to us. Our collection and use of personal information is governed by our <a href="/privacy-policy">Privacy Policy</a>, which is incorporated into these Terms by reference.
          </p>
        </section>

        <section>
          <h2>9. Disclaimers and Limitations of Liability</h2>
          
          <h3>9.1 Service "As Is"</h3>
          <p>
            The Platform is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that the Platform will be uninterrupted, secure, or error-free.
          </p>

          <h3>9.2 Educational Outcomes</h3>
          <p>
            We do not guarantee specific educational outcomes or results. Learning success depends on many factors including student effort, engagement, and individual circumstances.
          </p>

          <h3>9.3 Third-Party Content</h3>
          <p>
            We are not responsible for the accuracy, quality, or appropriateness of instructor-created content or third-party links.
          </p>

          <h3>9.4 Limitation of Liability</h3>
          <p>
            To the maximum extent permitted by law, UniqueBrains shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.
          </p>
        </section>

        <section>
          <h2>10. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless UniqueBrains, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
          </p>
          <ul>
            <li>Your use of the Platform</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of another party</li>
            <li>Content you submit to the Platform</li>
          </ul>
        </section>

        <section>
          <h2>11. Termination</h2>
          
          <h3>11.1 By You</h3>
          <p>You may terminate your account at any time by contacting us or using account settings.</p>

          <h3>11.2 By Us</h3>
          <p>We may suspend or terminate your account if you:</p>
          <ul>
            <li>Violate these Terms</li>
            <li>Engage in fraudulent or illegal activity</li>
            <li>Pose a security or legal risk</li>
            <li>Fail to pay required fees</li>
          </ul>

          <h3>11.3 Effect of Termination</h3>
          <p>
            Upon termination, your right to use the Platform ceases immediately. We may delete your account data in accordance with our Privacy Policy.
          </p>
        </section>

        <section>
          <h2>12. Modifications to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify users of significant changes by posting the updated Terms on the Platform and updating the "Last Updated" date. Your continued use after changes constitutes acceptance of the modified Terms.
          </p>
        </section>

        <section>
          <h2>13. Modifications to Service</h2>
          <p>
            We reserve the right to modify, suspend, or discontinue any part of the Platform at any time, with or without notice. We are not liable for any modification, suspension, or discontinuation of the service.
          </p>
        </section>

        <section>
          <h2>14. Dispute Resolution</h2>
          
          <h3>14.1 Informal Resolution</h3>
          <p>
            If you have a dispute with us, please contact us first at support@uniquebrains.org to attempt to resolve it informally.
          </p>

          <h3>14.2 Governing Law</h3>
          <p>
            These Terms are governed by the laws of the United States, without regard to conflict of law principles.
          </p>

          <h3>14.3 Arbitration</h3>
          <p>
            Any disputes that cannot be resolved informally shall be resolved through binding arbitration, except where prohibited by law.
          </p>
        </section>

        <section>
          <h2>15. General Provisions</h2>
          
          <h3>15.1 Entire Agreement</h3>
          <p>
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and UniqueBrains.
          </p>

          <h3>15.2 Severability</h3>
          <p>
            If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full effect.
          </p>

          <h3>15.3 Waiver</h3>
          <p>
            Our failure to enforce any right or provision of these Terms does not constitute a waiver of that right or provision.
          </p>

          <h3>15.4 Assignment</h3>
          <p>
            You may not assign or transfer these Terms without our written consent. We may assign our rights and obligations without restriction.
          </p>
        </section>

        <section>
          <h2>16. Contact Information</h2>
          <p>If you have questions about these Terms, please contact us:</p>
          <ul>
            <li>Email: support@uniquebrains.org</li>
            <li>Website: https://uniquebrains.org</li>
            <li>Instagram: @ouruniquebrains</li>
          </ul>
        </section>

        <section>
          <h2>17. Acknowledgment</h2>
          <p>
            By using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </p>
        </section>
      </div>
    </div>
  )
}

export default TermsOfService
